import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import Icon from "~/components/shared/Icon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import { Type } from "~/lib/pipeline/Step/Step";
import { useSharedHovercard } from "~/components/build/Show/pages/steps/list/components/useSharedHovercard";

const STEP_TYPE_ICON: Record<Type, string> = {
  [Type.Command]: "stepTypeCommand",
  [Type.Trigger]: "stepTypeTrigger",
  [Type.Group]: "stepTypeGroup",
  [Type.Input]: "stepTypeInput",
  [Type.Block]: "stepTypeBlock",
  [Type.Wait]: "stepTypeWait",
};

const STEP_TYPE_LABEL: Partial<Record<Type, string>> = {
  [Type.Command]: "Command",
  [Type.Trigger]: "Trigger",
  [Type.Input]: "Input",
  [Type.Block]: "Block",
  [Type.Wait]: "Wait",
};

function StepTypeBadge({ stepType, script }: { stepType: Type; script?: ReactNode }) {
  const iconName = STEP_TYPE_ICON[stepType];
  const fill = "none";
  const { isOpen, openDelay, handleOpenChange } = useSharedHovercard();
  const label = STEP_TYPE_LABEL[stepType];

  if (label) {
    return (
      <HoverCard
        open={isOpen}
        openDelay={openDelay}
        closeDelay={100}
        onOpenChange={handleOpenChange}
      >
        <HoverCardTrigger asChild={true}>
          <span className="text-charcoal-300 hover:text-charcoal-800 -mr-0.5 flex shrink-0 items-center p-1 transition-colors">
            <Icon icon={iconName} className="size-4" style={{ fill }} />
          </span>
        </HoverCardTrigger>
        <HoverCardPortal>
          <HoverCardContent
            className="pointer-events-none z-50"
            hideArrow={true}
            size="max-lg"
            side="top"
          >
            <p className="text-charcoal-600 m-0 text-xs font-medium">{label}</p>
            {script && (
              <pre className="text-xs-mono mt-2 whitespace-pre-wrap break-words font-mono">
                {script}
              </pre>
            )}
          </HoverCardContent>
        </HoverCardPortal>
      </HoverCard>
    );
  }

  return (
    <Icon icon={iconName} className="text-charcoal-300 ml-1 size-4 shrink-0" style={{ fill }} />
  );
}

// Depth-based spacer widths inserted between the arrow and icon to indent
// content while keeping the arrow at the leftmost position.
// depth 1: one arrow-width (1.75rem), depth 2: two arrow-widths (3.5rem)
const DEPTH_SPACER: Record<number, string> = {
  1: "1.75rem",
  2: "3.5rem",
};

interface StepRowProps {
  chevron?: ReactNode;
  icon?: ReactNode;
  stepType?: Type;
  label: ReactNode;
  labelClassName?: string;
  script?: ReactNode;
  stepStats?: ReactNode;
  meta?: ReactNode;
  timing?: ReactNode;
  className?: string;
  onClick?: () => void;
  depth?: number;
}

export function StepRow({
  chevron,
  icon,
  stepType,
  label,
  labelClassName,
  script,
  stepStats,
  meta,
  timing,
  className,
  onClick,
  depth = 0,
}: StepRowProps) {
  return (
    <div
      className={twMerge("flex w-full items-center", className)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? -1 : undefined}
    >
      {/* Zone 1: Fixed — chevron + spacer + icon */}
      <div className="mr-1 w-6 shrink-0">{chevron}</div>
      {DEPTH_SPACER[depth] && <div className="shrink-0" style={{ width: DEPTH_SPACER[depth] }} />}
      <div className="flex w-6 shrink-0 items-center justify-center pl-px">{icon}</div>
      {stepType && stepType !== Type.Group && <StepTypeBadge stepType={stepType} script={script} />}

      {/* Zone 2: Flexible — label + step stats */}
      <div className="ml-1.5 flex min-w-0 flex-auto items-center gap-2 self-stretch overflow-hidden py-1 text-left">
        <div
          className={twMerge(
            "text-charcoal-700 min-w-0 truncate font-medium group-hover/link:opacity-100 group-aria-[current=step]/link:opacity-100 group-data-[step-state='waiting_for_dependencies']/link:opacity-45 group-data-[step-state=ignored]/link:opacity-45",
            labelClassName,
          )}
        >
          {label}
        </div>
        {stepStats && <span className="sticky right-0 shrink-0 bg-inherit">{stepStats}</span>}
      </div>

      {/* Zone 3: Right-aligned — meta + timing */}
      {(meta || timing) && (
        <div className="container-hidden @xs/list:inline-flex ml-auto shrink-0 items-center gap-3 self-stretch pl-4 text-sm">
          {meta && (
            <span className="container-hidden @xl/list:inline-flex items-center gap-3">{meta}</span>
          )}
          {timing}
        </div>
      )}
    </div>
  );
}
